<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-form v-model="formValid" v-if="loaded" ref="form">
        <v-container grid-list-lg>
          <v-layout row wrap>
            <v-flex xs12 lg4>
              <date-time-picker label="Request Date" v-model="item.tripDate" @input="change"
                                :rules="rules.tripDate" required :disabled="!editable" :readonly="!editable" date-only />
            </v-flex>
            <v-flex xs12 lg4>
              <v-select label="Trip Expense Type" :items="tripExpenseTypesNormalized"
                        v-model="item.tripExpenseType"
                        item-value="tripExpenseType" @input="change"
                        item-text="displayName" required :disabled="!editable" :readonly="!editable"
                        v-if="tripExpenseTypes.map(x => x.tripExpenseType).includes(item.tripExpenseType)" />
              <v-text-field v-model="tripExpenseTypeNormalized" disabled readonly v-else />
            </v-flex>
            <v-flex xs12 lg4>
               <v-select label="Category"
                        :items="categories"
                        v-model="item.category"
                        item-value="id"
                        item-text="name"
                        @input="change"
                        :rules="rules.category"
                        return-object required
                        :disabled="!categoryLoaded || !editable" :readonly="!editable"
                        v-if="categories.map(x => x.id).includes(item.category.id) || categories.length > 0" />
              <v-text-field v-model="item.category.name" disabled readonly v-else />
            </v-flex>
            <v-flex xs12 lg3>
              <number-text-field label="Total" v-model="item.total" @input="change" prefix="Rp."
                            type="number" :rules="rules.total" :disabled="!editable" :readonly="!editable"  required />
            </v-flex>
            <v-flex xs12 lg3
                    v-if="typeof item.documentState !== 'undefined' && item.documentState === 'SUBMITTED' && editable">
              <number-text-field label="Adjustment" v-model="item.adjustment" @input="change" type="number" prefix="Rp."
                            :disabled="!editable" :readonly="!editable" required />
            </v-flex>
            <v-flex xs12 lg3>
              <number-text-field label="Sub Total" v-model="subTotal" type="number" @input="change" prefix="Rp."
                            required disabled readonly />
            </v-flex>
            <v-flex xs12 lg1>
              <v-text-field label="Percentage" v-model="percentage" type="number" @input="change"
                            min="0" max="100" suffix="%" disabled readonly />
            </v-flex>
            <v-flex xs12 lg3>
              <number-text-field label="Max. Claim" v-model="item.maxClaim" type="number" disabled readonly prefix="Rp."/>
            </v-flex>
            <v-flex xs12 lg2>
              <v-switch label="Have Invoice" v-model="item.haveInvoice" @change="change" required :disabled="!editable" />
            </v-flex>
            <v-flex xs12 lg12>
              <v-text-field multi-line rows="2" label="Remark"
                            v-model="item.remark"
                            @input="change"
                            :rules="rules.remark" :disabled="!editable" :readonly="!editable" />
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>
      <h2 v-else>Loading...</h2>
    </v-flex>
  </v-layout>
</template>

<script>
import InputForm from '@/components/InputForm'
import DateTimePicker from '@/components/lookup/DateTimePicker'
import Promise from 'promise-polyfill'
import Vuex from 'vuex'
import NumberTextField from '@/components/lookup/NumberTextField'

const tripExpenseStore = Vuex.createNamespacedHelpers('TripExpense')
const dialogsStore = Vuex.createNamespacedHelpers('Dialogs')

function calculateSubTotal (total, percentage) {
  return (total || 0) * (percentage / 100.0)
}

export default {
  name: 'business-trip-request-expense-form',
  components: {
    InputForm,
    DateTimePicker,
    NumberTextField
  },
  props: {
    deletable: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    value: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: 'Add/Edit Business Trip Request Expense'
    },
    refreshable: {
      Type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      formValid: false,
      item: this.value,
      loaded: false,
      categoryLoaded: false,
      rules: {
        tripDate: [
          v => (v !== null && new Date(v) instanceof Date) || 'Date is required'
        ],
        category: [
          v => (v.id !== '') || 'Category is required'
        ],
        subTotal: [
          v => parseInt(v) > 0 || 'Invalid Sub Total'
        ],
        total: [
          v => parseInt(v) > 0 || 'Invalid total'
        ],
        percentage: [
          v => (parseInt(v) >= 0 && parseInt(v) <= 100) || 'Invalid percentage'
        ],
        remark: [
          v => (typeof v === 'undefined' || v === null || v.length <= 500) || 'Max. 500 characters'
        ]
      }
    }
  },
  computed: {
    ...tripExpenseStore.mapState({
      tripExpenseTypes: 'items',
      tripExpenseCategories: 'categories'
    }),
    expenseCode () {
      const code = (this.tripExpenseTypes.find(x => x.tripExpenseType === this.item.tripExpenseType) || {}).code || ''
      return code
    },
    categories () {
      if (this.categoryLoaded) {
        return this.tripExpenseCategories[this.expenseCode] ||
          (this.value.tripExpenseType === 'ACCOMODATION' ? this.tripExpenseCategories['A'] : [])
      } else {
        return []
      }
    },
    tripExpenseTypesNormalized () {
      return this.tripExpenseTypes.map(x => { return {...x, displayName: x.tripExpenseType.replace('_', ' ')} })
    },
    tripExpenseTypeNormalized () {
      return this.item.tripExpenseType.replace('_', ' ')
    },
    percentage () {
      return this.item.category.allowedClaim || 0
    },
    subTotal () {
      const st = calculateSubTotal(this.item.total, this.percentage)
      if (parseInt(this.item.adjustment) !== 0 && this.item.adjustment) {
        return st + parseInt(this.item.adjustment)
      } else {
        if (typeof this.item.maxClaim !== 'undefined' && this.item.maxClaim > 0 && st > this.item.maxClaim) {
          return this.item.maxClaim
        } else {
          return st
        }
      }
    }
  },
  watch: {
    'item.tripExpenseType' (newValue, oldValue) {
      // load trip expense category
      const self = this
      self.categoryLoaded = false
      self.getExpenseCategory({vueinstance: self, category: self.expenseCode})
        .then(res => {
          self.categoryLoaded = true
        })
        .catch(err => {
          self.$error('Trip Expense Category ' + newValue + ': ' + err.message)
        })

      // reset category
      if (typeof oldValue !== 'undefined') {
        self.$set(self.item, 'category', {id: ''})
      }
    },
    'item.total' (newValue, oldValue) {
      if (typeof this.item.maxClaim !== 'undefined' && this.item.maxClaim > 0) {
        if (calculateSubTotal(newValue, this.percentage) > this.item.maxClaim) {
          this.warnAboutMaxClaim()
        }
      }
    },
    'item.category' (newValue, oldValue) {
      if (this.value.tripExpenseType === 'ACCOMODATION') {
        var remarkNotNull = this.item.remark !== null
        var n = remarkNotNull ? this.item.remark.indexOf(' | ') : -1
        var remark = remarkNotNull ? this.item.remark.substring(n >= 0 ? n + 3 : 0) : ''
        this.item.remark = this.item.category.name + ' | ' + remark
      }
    }
  },
  methods: {
    ...tripExpenseStore.mapActions({
      listTripExpense: 'list',
      getExpenseCategory: 'getCategory'
    }),
    ...dialogsStore.mapActions({
      showConfirmation: 'showConfirmation',
      closeConfirmation: 'closeConfirmation'
    }),
    loadResources () {
      const self = this
      const resources = [self.listTripExpense({vueinstance: self})]

      Promise.all(resources)
        .then(res => {
          // get code from trip expense types
          const tripExpense = res[0].find(x => x.tripExpenseType === self.value.tripExpenseType) ||
            (self.value.tripExpenseType === 'ACCOMODATION' ? {code: 'A'} : {})
          const expenseCode = tripExpense.code || ''
          if (expenseCode !== '') {
            self.getExpenseCategory({vueinstance: self, category: expenseCode})
              .then(res1 => {
                self.categoryLoaded = true
                self.loaded = true
              })
          } else {
            self.categoryLoaded = true
            self.loaded = true
          }
        })
    },
    close () {
      this.$emit('close')
    },
    save () {
      this.item.percentage = this.percentage
      this.item.subTotal = this.subTotal
      this.$emit('save', this.item)
    },
    _delete () {
      this.$emit('delete', this.item)
    },
    change () {
      this.item.percentage = this.percentage
      this.item.subTotal = this.subTotal
      this.$emit('input', this.item)
    },
    refresh () {
      this.$emit('refresh', this.item)
    },
    validate () {
      return this.$refs.form.validate()
    },
    warnAboutMaxClaim () {
      const self = this
      const okHandler = () => {
        self.closeConfirmation()
      }

      self.showConfirmation({
        title: 'Allowable Claim exceeded',
        message: 'Max. Allowable Claim ' + (self.item.maxClaim || 0) + ', automatically set it to ' + self.subTotal,
        buttons: [{label: 'OK', handler: okHandler}]
      })
    }
  },
  mounted () {
    this.loadResources()
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/bt-realization/BtRealizationExpenseForm.vue